import type { TFunction } from 'i18next'
import { BUSY_CODE } from './busy-retry'

// One place that turns a refused auth-service request into a sentence the
// person can act on (agion/Agion#2265). A page shows the translation of a known
// refusal, else the server's own sentence, else its own fallback key; never a
// machine code, a JSON parser message or an exception's text.

export const ACCOUNT_LOCKED_CODE = 'account_locked'
const RATE_LIMIT_CODE = 'rate_limit_exceeded'

// Server `error` values with a translation. "Invalid credentials" is the whole
// 401 answer to a wrong email or password (auth-service invalidLoginCredentials).
const KEY_BY_ERROR = new Map<string, string>([
  [BUSY_CODE, 'authErrors.busy'],
  [ACCOUNT_LOCKED_CODE, 'authErrors.accountPending'],
  ['Invalid credentials', 'authErrors.invalidCredentials'],
])

// A machine code such as rate_limit_exceeded; written for programs, never shown.
const MACHINE_CODE_RE = /^[a-z0-9]+(_[a-z0-9]+)*$/

const isSentence = (text: string) => text.trim() !== '' && !MACHINE_CODE_RE.test(text)

function parseRetryAfterSeconds(header: string | null): number | null {
  const value = header?.trim() ?? ''
  if (!/^\d+$/.test(value)) return null
  const seconds = Number(value)
  return seconds > 0 ? seconds : null
}

interface AuthRefusal {
  status: number
  serverError: string
  serverSentence: string
  retryAfterSeconds: number | null
  redirect: string
  jsonBody: boolean
}

/** A refused auth-service request. Its message is for logs; pages show authErrorMessage. */
export class AuthRequestError extends Error {
  readonly status: number
  /** The body's `error`: a code such as account_locked, or a sentence. */
  readonly serverError: string
  /** A sentence the server wrote for people, or '' when it wrote none. */
  readonly serverSentence: string
  /** Whole seconds from Retry-After, or null. */
  readonly retryAfterSeconds: number | null
  /** The body's `redirect`, e.g. /waitlist/pending, or ''. */
  readonly redirect: string
  /** False when the body was not a JSON object, e.g. a gateway's HTML page. */
  readonly jsonBody: boolean

  constructor(refusal: AuthRefusal) {
    super(`Auth request refused with HTTP ${refusal.status}`)
    this.name = 'AuthRequestError'
    this.status = refusal.status
    this.serverError = refusal.serverError
    this.serverSentence = refusal.serverSentence
    this.retryAfterSeconds = refusal.retryAfterSeconds
    this.redirect = refusal.redirect
    this.jsonBody = refusal.jsonBody
  }

  static async fromResponse(response: Response): Promise<AuthRequestError> {
    const body: unknown = await response.json().catch(() => null)
    const fields = body !== null && typeof body === 'object' && !Array.isArray(body)
      ? (body as Record<string, unknown>)
      : null
    const text = (name: string): string => {
      const value = fields?.[name]
      return typeof value === 'string' ? value : ''
    }
    const serverError = text('error')
    return new AuthRequestError({
      status: response.status,
      serverError,
      serverSentence: [text('message'), text('error_description'), serverError].find(isSentence) ?? '',
      retryAfterSeconds: parseRetryAfterSeconds(response.headers.get('Retry-After')),
      redirect: text('redirect'),
      jsonBody: fields !== null,
    })
  }

  /** The redirect when it is a path inside login-ui, else ''. */
  get appRedirect(): string {
    return /^\/(?![/\\])/.test(this.redirect) ? this.redirect : ''
  }
}

function rateLimitMessage(error: AuthRequestError, t: TFunction): string {
  return error.retryAfterSeconds === null
    ? t('authErrors.rateLimitedNoWait')
    : t('authErrors.rateLimited', { count: Math.ceil(error.retryAfterSeconds / 60) })
}

/** The sentence to show for a failed auth request, in the page's language. */
export function authErrorMessage(error: unknown, t: TFunction, fallbackKey: string): string {
  // fetch rejects with a TypeError when no answer arrived at all.
  if (error instanceof TypeError) return t('authErrors.network')
  if (!(error instanceof AuthRequestError)) return t(fallbackKey)

  if (error.serverError === RATE_LIMIT_CODE || (error.status === 429 && !error.serverSentence)) {
    return rateLimitMessage(error, t)
  }
  const key = KEY_BY_ERROR.get(error.serverError)
  if (key) return t(key)
  if (!error.jsonBody || error.status >= 500) return t('authErrors.unavailable')
  return error.serverSentence || t(fallbackKey)
}

// Refusal codes of POST /auth/password-reset and /auth/password-reset/confirm
// (agion/Agion#2375). Unknown, expired, used and retired tokens all answer
// invalid_token; the reset page shows its invalid-link state for it.
export const INVALID_TOKEN_CODE = 'invalid_token'
const INVALID_REQUEST_CODE = 'invalid_request'
const PASSWORD_REJECTED_CODE = 'password_rejected'
const PASSWORD_REUSED_CODE = 'password_reused'

/** True when the confirm step refused the link itself, not the password. */
export function isInvalidResetToken(error: unknown): boolean {
  return error instanceof AuthRequestError && error.serverError === INVALID_TOKEN_CODE
}

/** The two password reset calls: ask for a link, or set the password with it. */
export type PasswordResetStep = 'request' | 'confirm'

const RESET_FALLBACK_KEY: Record<PasswordResetStep, string> = {
  request: 'passwordReset.requestFailed',
  confirm: 'passwordReset.resetFailed',
}

/**
 * The sentence for a refused password reset step. The password policy's own
 * text (the password_rejected `message`) is shown inside a translated
 * sentence; every other refusal maps to a key or falls back to authErrorMessage.
 */
export function passwordResetErrorMessage(error: unknown, t: TFunction, step: PasswordResetStep): string {
  const fallbackKey = RESET_FALLBACK_KEY[step]
  if (!(error instanceof AuthRequestError)) return authErrorMessage(error, t, fallbackKey)

  if (error.status === 429) return rateLimitMessage(error, t)
  switch (error.serverError) {
    case INVALID_REQUEST_CODE:
      // The request step validates only the email's shape.
      return step === 'request' ? t('authErrors.invalidEmail') : t(fallbackKey)
    case INVALID_TOKEN_CODE:
      return t('passwordReset.linkExpired')
    case PASSWORD_REUSED_CODE:
      return t('passwordReset.passwordReused')
    case PASSWORD_REJECTED_CODE: {
      const reason = error.serverSentence.trim()
      return reason
        ? t('passwordReset.passwordRejected', { reason })
        : t('passwordReset.passwordRejectedNoReason')
    }
    default:
      return authErrorMessage(error, t, fallbackKey)
  }
}
